<script setup>
import { computed, watch, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAbaPilula } from 'dashboard/routes/dashboard/legal/useAbaPilula';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { LegalClientsApi } from 'dashboard/api/legalClients';
import { useConversationSidebarTab } from 'dashboard/composables/useConversationSidebarTab';

import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationAiSummary from './ConversationAiSummary.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactLegalProcesses from './contact/ContactLegalProcesses.vue';
import ContactUpcomingActivities from './contact/ContactUpcomingActivities.vue';
import ContactFinancialSummary from './contact/ContactFinancialSummary.vue';
import { abrirGavetaDeAssinatura } from 'dashboard/components-next/assinatura/useGavetaDeAssinatura';
import ContactNotes from './contact/ContactNotes.vue';
import ConversationAttachments from './contact/ConversationAttachments.vue';
import ConversationAttachmentsPanel from './contact/ConversationAttachmentsPanel.vue';
import { useAnexosDaConversa } from 'dashboard/composables/useAnexosDaConversa';
import ConversationInfo from './ConversationInfo.vue';
import LivroDeToques from './contact/LivroDeToques.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import PipelineSection from './PipelineSection.vue';
import FollowUpStatusCard from 'dashboard/components-next/FollowUp/FollowUpStatusCard.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import { corDoNome, iniciaisDoNome } from 'dashboard/helper/avatar';
import { corDoCanal } from 'dashboard/helper/corDoCanal';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  inboxId: { type: Number, default: undefined },
});

const { updateUISettings } = useUISettings();
const { t } = useI18n();
const store = useStore();

const activeTab = ref('info');
const tabs = [
  { id: 'info', label: 'Info', icon: 'i-lucide-user' },
  { id: 'crm', label: 'CRM', icon: 'i-lucide-kanban' },
  { id: 'notas', label: 'Notas', icon: 'i-lucide-sticky-note' },
  { id: 'hist', label: 'Histórico', icon: 'i-lucide-history' },
];

// O CONTEÚDO DA ABA CHEGA DO LADO DA ABA ESCOLHIDA (24/10/2026). As quatro abas são
// `v-if` independentes e trocavam secas; agora a que entra desliza da direita quando se
// anda para uma aba à direita, e da esquerda quando se volta — o mesmo sentido da pílula.
const direcaoDaAba = ref('dir');
watch(activeTab, (nova, antiga) => {
  const i = id => tabs.findIndex(aba => aba.id === id);
  direcaoDaAba.value = i(nova) >= i(antiga) ? 'dir' : 'esq';
});

// A pilula deslizante das quatro abas do painel — a mesma da lista de
// conversas, da ficha do processo e das Publicacoes. As quatro dividem a
// largura em partes iguais: sao alvos fixos numa coluna de 320px.
const caixaAbas = ref(null);
const { estiloPilula, pilulaPronta, prepararPilula, aoRolar } =
  useAbaPilula(caixaAbas);

watch(activeTab, async () => {
  await nextTick();
  aoRolar();
});

onMounted(prepararPilula);

const currentChat = useMapGetter('getSelectedChat');
const conversationId = computed(() => props.conversationId);

// "Mídia, links e docs". Os dados moram AQUI, e não dentro da linha nem do
// painel, porque os dois mostram a mesma coisa: a linha mostra o total, o
// painel mostra as listas. Se cada um buscasse por conta, os contadores
// divergiriam — o tipo de cópia que já causou três bugs neste módulo.
const {
  midias: anexosMidias,
  audios: anexosAudios,
  documentos: anexosDocumentos,
  links: anexosLinks,
  carregando: anexosCarregando,
  falhou: anexosFalhou,
  total: anexosTotal,
  carregar: carregarAnexos,
  limpar: limparAnexos,
} = useAnexosDaConversa(() => props.conversationId);

// `false` = card normal; `true` = o painel de anexos ocupa a coluna inteira.
const anexosAbertos = ref(false);

const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);
const conversationAdditionalAttributes = computed(
  () => currentConversationMetaData.value?.additional_attributes || {}
);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value) || {});

const additionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

// Sinal estrutural de "é cliente" (LegalClient), não confundir com Label
// manual — ver app/models/legal_client.rb (pedido do Victor 2026-07-17).
const isLegalClient = computed(
  () => !!contact.value.custom_attributes?.is_legal_client
);

// CARTÃO "FICHA DO CLIENTE" (Victor, 17/09/2026). O telefone já aparece no
// topo, embaixo do nome; a linha repetida deu lugar ao atalho para a ficha.
const fichaDoClienteId = ref(null);
watch(
  [contactId, isLegalClient],
  async ([id, ehCliente]) => {
    fichaDoClienteId.value = null;
    if (!id || !ehCliente) return;
    try {
      const { data } = await LegalClientsApi.getAll({ contact_id: id });
      if (contactId.value === id) {
        fichaDoClienteId.value = data.payload?.[0]?.id || null;
      }
    } catch {
      fichaDoClienteId.value = null;
    }
  },
  { immediate: true }
);

const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const contactProfileLink = computed(() => {
  const accountId = store.getters.getCurrentAccountId;
  return `/app/accounts/${accountId}/contacts/${contact.value.id}`;
});

// O canal da conversa, na cor da marca dele — o mesmo selinho do cartão da
// lista.
const caixaDaConversa = computed(
  () => store.getters['inboxes/getInbox'](currentChat.value?.inbox_id) || {}
);
const corDaCaixa = computed(
  () => corDoCanal(caixaDaConversa.value) || 'rgb(255 255 255 / 30%)'
);

const fotoFalhou = ref(false);
watch(
  () => contact.value.thumbnail,
  () => {
    fotoFalhou.value = false;
  }
);

const accountLabels = useMapGetter('labels/getLabels');
const conversationLabels = computed(() => currentChat.value?.labels || []);
// A Label manual "cliente" (pré-existente, frágil) é escondida daqui — o
// badge fixo (isLegalClient) já cobre esse sinal, com fonte de verdade real
// (LegalClient), não uma tag solta. Ver também ContactLabels.vue.
const activeLabels = computed(() =>
  accountLabels.value.filter(
    ({ title }) =>
      conversationLabels.value.includes(title) &&
      title.toLowerCase() !== 'cliente'
  )
);

const formattedLocation = computed(() => {
  const { city, country } = additionalAttributes.value;
  return [city, country].filter(Boolean).join(', ');
});

const getContactDetails = () => {
  if (contactId.value) {
    // Sem o detalhe novo, a ficha segue com o contato que a conversa trouxe.
    store.dispatch('contacts/show', { id: contactId.value }).catch(() => {});
  }
};

watch(contactId, (newId, prevId) => {
  if (newId && newId !== prevId) getContactDetails();
});

// Trocou de conversa: os anexos são outros. Fecha o painel também — ficar
// dentro da galeria de uma conversa que não está mais aberta desorienta.
watch(
  () => props.conversationId,
  () => {
    anexosAbertos.value = false;
    limparAnexos();
    carregarAnexos();
  }
);

const closeContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const markAsUnread = async () => {
  try {
    await store.dispatch('markMessagesUnread', { id: conversationId.value });
    closeContactPanel();
  } catch {
    // ignore
  }
};

// O visto de "copiei" nasce no próprio botão (`.advup-acao--confirmado`):
// só o aviso lá no canto da tela não dizia QUAL coisa foi copiada.
const telefoneCopiado = ref(false);
const copyPhone = async () => {
  if (!contact.value.phone_number) return;
  try {
    await copyTextToClipboard(contact.value.phone_number);
    telefoneCopiado.value = true;
    useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
    setTimeout(() => {
      telefoneCopiado.value = false;
    }, 1600);
  } catch {
    useAlert(t('CONTACT_PANEL.COPY_FAILED'));
  }
};

// Lightbox do avatar — mostra a miniatura (já em cache/rápida) na hora,
// troca pra versão grande assim que ela carregar em segundo plano, evitando
// o delay de ~2s do processamento do ActiveStorage na primeira geração.
const showAvatarLightbox = ref(false);
const lightboxSrc = ref('');

const openAvatarLightbox = () => {
  if (!contact.value.thumbnail) return;
  lightboxSrc.value = contact.value.thumbnail;
  showAvatarLightbox.value = true;

  const largeUrl = contact.value.avatar_url_large;
  if (largeUrl && largeUrl !== contact.value.thumbnail) {
    const preload = new Image();
    preload.onload = () => {
      lightboxSrc.value = largeUrl;
    };
    preload.src = largeUrl;
  }
};

// Inline edit do nome
const editingName = ref(false);
const editNameValue = ref('');
const nameInputRef = ref(null);

// 🔴 NOME DE CLIENTE NÃO SE EDITA PELO CHAT (Victor, 21/09/2026).
//
// O nome que chega aqui é o que a pessoa escolheu no WhatsApp dela
// ("EVELYN ANJOS FESTRAETS"), e o chat deixava trocar com um clique. Só que,
// quando o contato é cliente, esse nome é o do CADASTRO — o mesmo que sai na
// petição, no contrato e na cobrança. Corrigir pelo chat mudava o cadastro
// inteiro a partir da tela onde menos se percebe o estrago.
//
// Quem é cliente edita o nome na ficha, onde o campo está ao lado do CPF e do
// resto do que identifica a pessoa.
const podeEditarNome = computed(() => !isLegalClient.value);

const startEditName = () => {
  if (!podeEditarNome.value) return;

  editNameValue.value = contact.value.name || '';
  editingName.value = true;
  nextTick(() => nameInputRef.value?.select());
};

const cancelEditName = () => {
  editingName.value = false;
  editNameValue.value = '';
};

const saveContactName = async () => {
  const newName = editNameValue.value.trim();
  if (!newName || newName === contact.value.name) {
    cancelEditName();
    return;
  }
  try {
    await store.dispatch('contacts/update', {
      id: contact.value.id,
      name: newName,
    });
    useAlert(t('CONTACT_PANEL.NAME_UPDATED'));
  } catch {
    useAlert(t('CONTACT_PANEL.NAME_UPDATE_ERROR'));
  } finally {
    editingName.value = false;
  }
};

const { pendingSidebarTab, consumeSidebarTab } = useConversationSidebarTab();

// Quando o painel monta (ou já está montado), aplica a aba pedida pelo header.
const applyPendingTab = () => {
  const tab = consumeSidebarTab();
  if (tab) activeTab.value = tab;
};
// Caso o painel já esteja aberto, reage ao novo pedido.
watch(pendingSidebarTab, tab => {
  if (tab) {
    activeTab.value = tab;
    consumeSidebarTab();
  }
});

onMounted(() => {
  getContactDetails();
  // Sem os atributos, a aba Info só não mostra os campos extras.
  store.dispatch('attributes/get', 0).catch(() => {});
  applyPendingTab();
  // O contador precisa existir com o painel fechado — é ele que diz se vale
  // abrir.
  carregarAnexos();
});

// Há o que mostrar em "Informação da conversa"? Atributo de conversa
// cadastrado, ou os dados do navegador que só o chat do site manda.
const atributosPorModelo = useMapGetter('attributes/getAttributesByModel');
const temInfoDaConversa = computed(() => {
  if (atributosPorModelo.value('conversation_attribute').length) return true;
  const conversa = conversationAdditionalAttributes.value || {};
  const contato = contactAdditionalAttributes.value || {};
  return Boolean(
    conversa.initiated_at ||
      conversa.browser ||
      conversa.referer ||
      contato.created_at_ip
  );
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="w-full flex flex-col h-full overflow-hidden">
    <!-- ═══ ANEXOS — OCUPA A COLUNA INTEIRA ═══
         No lugar do card, não por cima dele. Pedido do Victor (2026-08-07),
         com o Digisac na tela: "quero que abra na posição onde está o card,
         não um modal no meio; tenho até uma setinha pra voltar". -->
    <ConversationAttachmentsPanel
      v-if="anexosAbertos"
      :midias="anexosMidias"
      :audios="anexosAudios"
      :documentos="anexosDocumentos"
      :links="anexosLinks"
      :carregando="anexosCarregando"
      :falhou="anexosFalhou"
      @voltar="anexosAbertos = false"
      @tentar-de-novo="carregarAnexos"
    />

    <template v-else>
      <!-- ═══ O TOPO DA FICHA ═══
           Redesenhado em 16/09/2026 (auditoria: nota 6). Era tudo empilhado
           no centro — avatar de 96px, nome, telefone, "Desde há…", selo — e
           as abas começavam na metade da coluna. Agora o rosto e o nome ficam
           lado a lado sobre a arte, e o que se quer saber de relance vira uma
           faixa de três fatos. -->
      <div class="cp-hero">
        <div class="cp-hero__barra">
          <span
            v-if="caixaDaConversa.id"
            class="cp-canal"
            :style="{ '--canal': corDaCaixa }"
          >
            <span class="cp-canal__ico" aria-hidden="true">
              <ChannelIcon :inbox="caixaDaConversa" />
            </span>
            <span class="cp-canal__nome">{{ caixaDaConversa.name }}</span>
          </span>
          <span class="cp-hero__acoes">
            <a
              v-tooltip.bottom="t('CONTACT_PANEL.OPEN_FULL_PROFILE')"
              :href="contactProfileLink"
              target="_blank"
              rel="noopener noreferrer"
              class="cp-hero__acao"
              :aria-label="t('CONTACT_PANEL.OPEN_FULL_PROFILE')"
            >
              <i class="i-lucide-external-link" />
            </a>
            <button
              v-tooltip.bottom="t('CONTACT_PANEL.CLOSE')"
              type="button"
              class="cp-hero__acao"
              :aria-label="t('CONTACT_PANEL.CLOSE')"
              @click="closeContactPanel"
            >
              <i class="i-lucide-x" />
            </button>
          </span>
        </div>

        <div class="cp-id">
          <button
            type="button"
            class="cp-avatar"
            :class="{ 'cp-avatar--foto': contact.thumbnail && !fotoFalhou }"
            :disabled="!contact.thumbnail || fotoFalhou"
            :aria-label="t('CONTACT_PANEL.SEE_PHOTO')"
            @click="openAvatarLightbox"
          >
            <span
              class="advup-avatar cp-avatar__rosto"
              :class="`advup-avatar--${corDoNome(contact.name)}`"
              aria-hidden="true"
            >
              {{ iniciaisDoNome(contact.name) }}
              <img
                v-if="contact.thumbnail && !fotoFalhou"
                :src="contact.thumbnail"
                alt=""
                @error="fotoFalhou = true"
              />
            </span>
          </button>

          <div class="cp-id__txt">
            <div class="cp-id__linha">
              <input
                v-if="editingName"
                ref="nameInputRef"
                v-model="editNameValue"
                class="cp-nome-editor reset-base"
                type="text"
                autocomplete="off"
                autocorrect="off"
                spellcheck="false"
                :aria-label="t('CONTACT_PANEL.CLICK_TO_EDIT_NAME')"
                @keydown.enter="saveContactName"
                @keydown.esc="cancelEditName"
                @blur="saveContactName"
              />
              <h3
                v-else
                v-tooltip.bottom="
                  podeEditarNome
                    ? t('CONTACT_PANEL.CLICK_TO_EDIT_NAME')
                    : t('CONTACT_PANEL.NAME_FROM_CLIENT_FILE')
                "
                class="cp-nome"
                :class="{ 'cp-nome--travado': !podeEditarNome }"
                @click="startEditName"
              >
                {{ contact.name }}
              </h3>
              <button
                v-if="!editingName && podeEditarNome"
                type="button"
                class="cp-editar"
                :aria-label="t('CONTACT_PANEL.CLICK_TO_EDIT_NAME')"
                @click="startEditName"
              >
                <i class="i-lucide-pencil" />
              </button>
            </div>
            <!-- O telefone do topo é o único da aba: tocar copia. -->
            <button
              v-if="contact.phone_number"
              v-tooltip.bottom="t('CONTACT_PANEL.COPY_PHONE')"
              type="button"
              class="cp-hero__fone"
              :aria-label="t('CONTACT_PANEL.COPY_PHONE')"
              @click="copyPhone"
            >
              <i
                :class="telefoneCopiado ? 'i-lucide-check' : 'i-lucide-phone'"
                aria-hidden="true"
              />
              {{ contact.phone_number }}
            </button>
            <span v-if="isLegalClient" class="advup-selo-cliente cp-selo">
              <span class="i-lucide-shield-check" />
              {{ t('CONTACT_PANEL.LEGAL_CLIENT_BADGE') }}
            </span>
          </div>
        </div>

        <!-- "Marcar como não lida" é ação da CAIXA DE ENTRADA, não do
             cliente: discreta, vazada e do tamanho do que é. Os botões
             Mensagem / Ligar / Copiar saíram do topo a pedido do Victor
             (07/08: "ocupa menos espaço no card") e não voltam. -->
        <div class="cp-topo-acoes">
          <button type="button" class="cp-nao-lida" @click="markAsUnread">
            <span
              class="i-lucide-mail-plus cp-nao-lida__ico"
              aria-hidden="true"
            />
            {{ t('CONTACT_PANEL.MARK_UNREAD') }}
          </button>
          <!-- Minutas: a mesma gaveta do compositor, a partir do cliente. -->
          <button
            v-if="conversationId"
            v-tooltip.bottom="t('ASSINATURAS.BOTAO_DICA')"
            type="button"
            class="cp-nao-lida"
            @click="abrirGavetaDeAssinatura(conversationId)"
          >
            <span class="i-ph-signature cp-nao-lida__ico" aria-hidden="true" />
            {{ t('ASSINATURAS.BOTAO') }}
          </button>
        </div>

        <!-- "Mídia, links e docs" colado no card do contato (Victor,
             07/08: no fim da aba Info ficava "muito lá embaixo"). -->
        <ConversationAttachments
          v-if="conversationId"
          :total="anexosTotal"
          @abrir="anexosAbertos = true"
        />
      </div>

      <!-- ═══ TAB BAR ═══ -->
      <div class="cp-abas">
        <div ref="caixaAbas" class="cp-abas__caixa">
          <span
            class="aba-pilula"
            :class="{ 'aba-pilula--viva': pilulaPronta }"
            :style="estiloPilula"
            aria-hidden="true"
          />
          <button
            v-for="tab in tabs"
            :key="tab.id"
            class="aba cp-aba"
            :class="{ 'aba--on': activeTab === tab.id }"
            role="tab"
            :aria-selected="activeTab === tab.id"
            @click="activeTab = tab.id"
          >
            <i class="aba-ico" :class="tab.icon" />
            {{ tab.label }}
          </button>
        </div>
      </div>

      <!-- ═══ TAB CONTENT ═══ -->
      <div class="flex-1 overflow-y-auto min-h-0">
        <!-- ── INFO TAB ── -->
        <div
          v-if="activeTab === 'info'"
          class="p-3 flex flex-col gap-2 cp-aba"
          :class="`cp-aba--${direcaoDaAba}`"
        >
          <!-- Resumo IA -->
          <ConversationAiSummary :conversation-id="conversationId" />

          <!-- Follow-up automático (advup) -->
          <FollowUpStatusCard :conversation-id="conversationId" />

          <!-- Processos vinculados (advup) -->
          <div>
            <p class="advup-rotulo advup-rotulo--secao cp-secao">
              {{ t('CONTACT_PANEL.LABEL_LEGAL_PROCESSES') }}
            </p>
            <ContactLegalProcesses :contact-id="contactId" />
          </div>

          <!-- Ficha do cliente -->
          <router-link
            v-if="fichaDoClienteId"
            :to="{
              name: 'legal_client_show',
              params: { clientId: fichaDoClienteId },
            }"
            class="cp-linha cp-linha--azul cp-ficha"
          >
            <span class="cp-linha__ico" aria-hidden="true">
              <i class="i-lucide-id-card" />
            </span>
            <div class="flex-1 min-w-0">
              <p class="cp-linha__valor">
                {{ t('CONTACT_PANEL.CLIENT_RECORD') }}
              </p>
              <p class="advup-rotulo cp-rot">
                {{ t('CONTACT_PANEL.CLIENT_RECORD_HINT') }}
              </p>
            </div>
            <i
              class="i-lucide-chevron-right cp-ficha__seta"
              aria-hidden="true"
            />
          </router-link>

          <!-- Email -->
          <div v-if="contact.email" class="cp-linha cp-linha--teal">
            <span class="cp-linha__ico" aria-hidden="true">
              <i class="i-lucide-mail" />
            </span>
            <div class="flex-1 min-w-0">
              <p class="advup-rotulo cp-rot">
                {{ t('CONTACT_PANEL.LABEL_EMAIL') }}
              </p>
              <a
                :href="`mailto:${contact.email}`"
                class="cp-linha__valor truncate block"
              >
                {{ contact.email }}
              </a>
            </div>
          </div>

          <!-- Company -->
          <div
            v-if="additionalAttributes.company_name"
            class="cp-linha cp-linha--iris"
          >
            <span class="cp-linha__ico" aria-hidden="true">
              <i class="i-lucide-building-2" />
            </span>
            <div class="flex-1 min-w-0">
              <p class="advup-rotulo cp-rot">
                {{ t('CONTACT_PANEL.LABEL_COMPANY') }}
              </p>
              <p class="cp-linha__valor">
                {{ additionalAttributes.company_name }}
              </p>
            </div>
          </div>

          <!-- Location -->
          <!-- Localização: era uma linha escrita à parte, com o âmbar do
               Tailwind (`amber-500`, fora da nossa escala) e outro raio. Agora
               é a mesma linha das outras, no tom âmbar. -->
          <div v-if="formattedLocation" class="cp-linha cp-linha--ambar">
            <span class="cp-linha__ico" aria-hidden="true">
              <i class="i-lucide-map-pin" />
            </span>
            <div class="flex-1 min-w-0">
              <p class="advup-rotulo cp-rot">
                {{ t('CONTACT_PANEL.LABEL_LOCATION') }}
              </p>
              <p class="cp-linha__valor">{{ formattedLocation }}</p>
            </div>
          </div>

          <!-- Tags -->
          <div v-if="activeLabels.length" class="mt-1">
            <p class="advup-rotulo advup-rotulo--secao cp-secao">
              {{ t('CONTACT_PANEL.LABEL_TAGS') }}
            </p>
            <!-- A etiqueta mostra a cor dela num ponto, e o nome em texto
                 comum: branco sobre a cor que a pessoa escolheu reprovava em
                 contraste nas cores claras (amarelo, verde-limão). -->
            <div class="cp-etiquetas">
              <span
                v-for="label in activeLabels"
                :key="label.id"
                class="advup-etq cp-etiqueta"
              >
                <span
                  class="cp-etiqueta__ponto"
                  :style="{ backgroundColor: label.color }"
                  aria-hidden="true"
                />
                {{ label.title.replace(/-/g, ' ') }}
              </span>
            </div>
          </div>

          <!-- Atributos do contato -->
          <div class="mt-1">
            <p class="advup-rotulo advup-rotulo--secao cp-secao">
              {{ t('CONTACT_PANEL.LABEL_CONTACT_ATTRIBUTES') }}
            </p>
            <CustomAttributes
              attribute-type="contact_attribute"
              attribute-from="conversation_contact_panel"
              :contact-id="contact.id"
              empty-state-message=""
            />
          </div>

          <!-- Atributos da conversa. Só com conteúdo (05/10/2026): em produção
               não há atributo de conversa cadastrado e nenhum canal manda
               dados de navegador (isso é do chat do site) — o título
               "Informação da conversa" ficava sozinho, sem nada embaixo, em
               toda conversa. -->
          <div v-if="temInfoDaConversa" class="mt-1">
            <p class="advup-rotulo advup-rotulo--secao cp-secao">
              {{ t('CONTACT_PANEL.LABEL_CONVERSATION_INFO') }}
            </p>
            <ConversationInfo
              :conversation-attributes="conversationAdditionalAttributes"
              :contact-attributes="contactAdditionalAttributes"
            />
          </div>

          <!-- Compromissos do cliente, ACIMA do financeiro (pedido do Victor,
               02/09). Fecha o ciclo do botão "Agendar atendimento": marcou na
               conversa, aparece aqui. Some inteiro quando não há nenhum. -->
          <ContactUpcomingActivities
            :contact-id="contact.id"
            :conversation-id="conversationId"
            :contact="contact"
          />

          <!-- Situação financeira do cliente. Some inteiro para quem não é
               admin, e a restrição real é do servidor, não deste v-if. -->
          <ContactFinancialSummary
            :contact-id="contact.id"
            :contact-name="contact.name"
            :eh-cliente="isLegalClient"
            :conversation-id="conversationId"
          />
        </div>

        <!-- ── CRM TAB ── -->
        <div
          v-if="activeTab === 'crm'"
          class="p-3 flex flex-col gap-4 cp-aba"
          :class="`cp-aba--${direcaoDaAba}`"
        >
          <div>
            <p class="advup-rotulo advup-rotulo--secao cp-secao">
              {{ t('CONTACT_PANEL.LABEL_PIPELINE') }}
            </p>
            <PipelineSection :conversation-id="conversationId" />
          </div>

          <div class="cp-filete" />

          <div>
            <p class="advup-rotulo advup-rotulo--secao cp-secao">
              {{ t('CONTACT_PANEL.CONTACT_LABELS') }}
            </p>
            <ContactLabels :contact-id="contactId" />
          </div>

          <div class="cp-filete" />

          <div>
            <p class="advup-rotulo advup-rotulo--secao cp-secao">
              {{ t('CONTACT_PANEL.LABEL_ASSIGNMENT') }}
            </p>
            <ConversationAction
              :conversation-id="conversationId"
              :inbox-id="inboxId"
            />
          </div>
        </div>

        <!-- ── NOTAS TAB ── -->
        <div
          v-if="activeTab === 'notas'"
          class="p-3 cp-aba"
          :class="`cp-aba--${direcaoDaAba}`"
        >
          <ContactNotes :contact-id="contactId" />
        </div>

        <!-- ── HISTÓRICO TAB ── -->
        <div
          v-if="activeTab === 'hist'"
          class="p-3 flex flex-col gap-3 cp-aba"
          :class="`cp-aba--${direcaoDaAba}`"
        >
          <!-- Fatia 1b: o que o advup mandou sozinho, o que segurou e por quê. -->
          <LivroDeToques
            v-if="conversationId"
            :conversation-id="conversationId"
          />
          <div v-if="contact.id">
            <p class="advup-rotulo advup-rotulo--secao cp-secao">
              {{ t('CONTACT_PANEL.LABEL_PREVIOUS_CONVERSATIONS') }}
            </p>
            <ContactConversations
              :contact-id="contact.id"
              :conversation-id="conversationId"
            />
          </div>
        </div>
      </div>

      <!-- Lightbox do avatar em tamanho grande -->
      <Teleport to="body">
        <div
          v-if="showAvatarLightbox"
          class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md"
          @click="showAvatarLightbox = false"
        >
          <button
            type="button"
            class="cp-hero__acao cp-luz__fechar"
            :aria-label="t('CONTACT_PANEL.CLOSE')"
            @click="showAvatarLightbox = false"
          >
            <span class="i-lucide-x size-5" />
          </button>
          <img
            :src="lightboxSrc"
            :alt="contact.name"
            class="w-[min(95vw,560px)] h-[min(95vh,560px)] rounded-lg object-cover shadow-2xl"
            @click.stop
          />
        </div>
      </Teleport>
    </template>
  </div>
</template>

<style scoped>
/* A aba que entra (24/10/2026): desliza 12px do lado da aba escolhida e acende. Só
   transform e opacity; com "reduzir movimento" fica só o esmaecer. */
.cp-aba {
  animation: cp-aba-dir var(--mov-3) var(--mov-curva) both;
}

.cp-aba--esq {
  animation-name: cp-aba-esq;
}

@keyframes cp-aba-dir {
  from {
    opacity: 0;
    transform: translate3d(12px, 0, 0);
  }
}

@keyframes cp-aba-esq {
  from {
    opacity: 0;
    transform: translate3d(-12px, 0, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cp-aba {
    animation: none;
  }
}

/* Painel do contato.
 *
 * O que tinha de cravado aqui: o degrade do topo (#0f172a -> #162032 ->
 * #1e293b), o telefone (#94a3b8), o "desde" (#64748b), o selo de cliente
 * (#1f6feb -> #0b3d8f), o botao de nao lida (#2563eb + #93c5fd), a aba ativa
 * (#2563eb) e os ladrilhos de telefone, e-mail e empresa. Nenhum deles muda
 * de tema, e `emerald` e `violet-500` nem sao gerados neste bundle — o
 * ladrilho do E-MAIL saia SEM cor de fundo. */

/* ── Topo ─────────────────────────────────────────────────────────────── */
.cp-hero {
  position: relative;
  flex-shrink: 0;
  padding: var(--esp-3) var(--esp-4) var(--esp-4);
  /* Mesmo degrade navy -> azul do selo da marca e do topo dos modais: o
     painel abre com a mesma assinatura do resto do produto. */
  background: linear-gradient(
    160deg,
    rgb(15 23 42),
    rgb(23 37 71) 65%,
    rgb(30 51 100)
  );
  /* `isolate` para a arte poder ficar atrás do conteúdo com z-index negativo
     sem escapar para trás do painel inteiro. */
  isolation: isolate;
}

/* A ARTE DA CASA ATRÁS DO AZUL (Victor, 16/09/2026: "dá para colocar essa
   deusa da justiça, mas vendo a opacidade certinha, para não ficar muito
   forte").
   A arte é a que o Victor mandou nesta rodada, guardada em
   `public/brand-assets/justica-fundo.webp` (900px, webp, 26KB). Fica ancorada
   à direita porque é lá que a figura está na foto — o lado esquerdo dela é
   fundo limpo, que é exatamente o que o nome do cliente precisa atrás. */
.cp-hero::before {
  content: '';
  position: absolute;
  z-index: -1;
  inset: 0;
  /* O degrade não é uniforme de propósito: ele é OPACO onde o texto passa
     (esquerda e centro, 97%) e abre para 38% no canto direito, que é onde a
     figura aparece. Com um véu parelho a arte sumia; sem ele, a balança
     cruzava o nome do cliente. */
  background:
    linear-gradient(
      105deg,
      rgb(15 23 42 / 97%) 25%,
      rgb(20 32 60 / 78%) 55%,
      rgb(24 40 78 / 38%) 100%
    ),
    url('/brand-assets/justica-fundo.webp') right center / cover no-repeat;
  /* A arte é TEXTURA, não ilustração: o que se reconhece é a silhueta da
     dama da justiça no canto direito, não a foto. */
  opacity: 1;
  pointer-events: none;
}

/* ── A faixa de cima: o canal e as duas ações ──────────────────────────── */
.cp-hero__barra {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--esp-2);
  min-height: 32px;
}

/* O canal da conversa em vidro, com o ícone na cor da marca dele — o mesmo
   selinho do cartão da lista. */
.cp-canal {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  min-width: 0;
  height: 26px;
  padding: 0 var(--esp-2) 0 3px;
  border: 1px solid rgb(255 255 255 / 12%);
  border-radius: var(--r-redondo);
  background: rgb(15 23 42 / 45%);
  backdrop-filter: blur(6px);
}

.cp-canal__ico {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--r-redondo);
  background: var(--canal);
  color: rgb(255 255 255);
}

.cp-canal__ico :deep(*) {
  width: 11px;
  height: 11px;
}

.cp-canal__nome {
  overflow: hidden;
  font-size: var(--txt-rotulo);
  font-weight: var(--peso-rotulo);
  letter-spacing: var(--rastro-rotulo);
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
  color: rgb(255 255 255 / 78%);
}

.cp-hero__acoes {
  display: flex;
  flex-shrink: 0;
  gap: var(--esp-1);
  margin-inline-start: auto;
}

/* 🔴 OS BOTÕES DO CANTO SOBRE A ARTE (Victor, 16/09/2026: "melhore a
   visualização desses dois botões"). Chão próprio — navy translúcido com
   desfoque do que está atrás —, aro para recortar da arte, ícone quase
   branco, 32px de alvo. */
.cp-hero__acao {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 1px solid rgb(255 255 255 / 16%);
  border-radius: var(--r-redondo);
  background: rgb(15 23 42 / 55%);
  backdrop-filter: blur(6px);
  color: rgb(255 255 255 / 88%);
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva),
    scale var(--mov-1) var(--mov-curva);
}

.cp-hero__acao > i {
  width: var(--ico-1);
  height: var(--ico-1);
}

.cp-hero__acao:hover {
  border-color: rgb(255 255 255 / 34%);
  background: rgb(15 23 42 / 80%);
  color: rgb(255 255 255);
}

.cp-hero__acao:focus-visible {
  outline: 2px solid rgb(255 255 255 / 70%);
  outline-offset: 2px;
}

.cp-hero__acao:active {
  scale: 0.92;
}

/* ── O rosto em cima, o nome embaixo ──────────────────────────────────────
   Lado a lado, o nome longo cortava em "Victor César Duarte [TEST…" — o dado
   que identifica o cliente. Empilhado, o nome ganha a largura toda e quebra
   em até duas linhas (16/09/2026). */
.cp-id {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--esp-3);
  margin-top: var(--esp-1);
  text-align: center;
}

.cp-avatar {
  position: relative;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  border-radius: var(--r-redondo);
  background: none;
  cursor: default;
  animation: cpAvatarChega var(--mov-4) var(--mov-curva-pulo) both;
}

.cp-avatar--foto {
  cursor: zoom-in;
}

.cp-avatar:focus-visible {
  outline: 2px solid rgb(255 255 255 / 70%);
  outline-offset: 3px;
}

/* O rosto com anel duplo: o navy recorta da arte, e o aro em degradê da marca
   é o que dá acabamento. */
.cp-avatar__rosto {
  /* O degradê começa num azul fundo, não no navy da marca: sobre o topo
     navy as iniciais sumiam. */
  --marca-1: 30 64 175;

  width: 76px;
  height: 76px;
  font-size: var(--txt-num);
  box-shadow:
    0 0 0 3px rgb(15 23 42),
    0 0 0 5px rgb(var(--blue-9) / 55%),
    0 10px 24px -8px rgb(0 0 0 / 60%);
  transition: scale var(--mov-2) var(--mov-curva-pulo);
}

.cp-avatar--foto:hover .cp-avatar__rosto {
  scale: 1.04;
}

@keyframes cpAvatarChega {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
}

.cp-id__txt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--esp-2);
  width: 100%;
  min-width: 0;
  animation: cpNomeChega var(--mov-4) var(--mov-curva) backwards;
  animation-delay: var(--mov-1);
}

@keyframes cpNomeChega {
  from {
    opacity: 0;
    translate: 0 6px;
  }
}

/* O lápis fica fora do fluxo: assim o nome se centraliza de verdade. */
.cp-id__linha {
  position: relative;
  max-width: calc(100% - 3rem);
}

.cp-nome {
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  font-size: var(--txt-titulo);
  font-weight: var(--peso-titulo);
  line-height: var(--linha-apertada);
  color: rgb(255 255 255);
  text-wrap: balance;
  overflow-wrap: anywhere;
  cursor: text;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

/* Sem cursor de texto: o cursor é a primeira coisa que promete edição, e aqui
   não há o que prometer. */
.cp-nome--travado {
  cursor: default;
}

.cp-nome::first-letter {
  text-transform: uppercase;
}

.cp-nome-editor {
  width: 18rem;
  max-width: 100%;
  height: 34px;
  text-align: center;
  padding: 0 var(--esp-2);
  border: 1px solid rgb(255 255 255 / 30%);
  border-radius: var(--r-2);
  outline: none;
  background: rgb(255 255 255 / 10%);
  font-size: var(--txt-lista);
  font-weight: var(--peso-forte);
  color: rgb(255 255 255);
}

.cp-nome-editor:focus {
  border-color: rgb(255 255 255 / 60%);
}

.cp-editar {
  position: absolute;
  top: 50%;
  left: calc(100% + var(--esp-1));
  translate: 0 -50%;
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 0;
  border-radius: var(--r-1);
  background: none;
  color: rgb(255 255 255 / 45%);
  cursor: pointer;
  opacity: 0;
  transition:
    color var(--mov-1) var(--mov-curva),
    opacity var(--mov-1) var(--mov-curva);
}

.cp-id:hover .cp-editar,
.cp-editar:focus-visible {
  opacity: 1;
}

.cp-editar:hover {
  color: rgb(255 255 255 / 95%);
}

.cp-editar > i {
  width: 12px;
  height: 12px;
}

@media (hover: none) {
  .cp-editar {
    opacity: 1;
  }
}

.cp-hero__fone {
  display: flex;
  align-items: center;
  gap: var(--esp-1);
  margin: 0;
  padding: var(--esp-1) var(--esp-3);
  border: 1px solid rgb(255 255 255 / 12%);
  border-radius: var(--r-redondo);
  background: rgb(15 23 42 / 45%);
  backdrop-filter: blur(8px);
  font-size: var(--txt-nota);
  font-variant-numeric: tabular-nums;
  color: rgb(255 255 255 / 70%);
}

.cp-hero__fone {
  font-family: inherit;
  cursor: pointer;
  transition: color var(--mov-1) var(--mov-curva);
}

.cp-hero__fone:hover {
  color: rgb(255 255 255 / 92%);
}

.cp-hero__fone:focus-visible {
  outline: 2px solid rgb(255 255 255 / 70%);
  outline-offset: 2px;
}

.cp-hero__fone > i {
  width: 11px;
  height: 11px;
}

/* O selo é a peça do produto; aqui só o lugar dele. */
.cp-selo {
  margin-top: 0;
}

/* "Marcar como não lida": ação de caixa de entrada, discreta — vazada e do
   tamanho do texto, e não uma faixa cheia de ponta a ponta. */
.cp-topo-acoes {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--esp-2);
  margin-top: var(--esp-3);
}

.cp-nao-lida {
  display: flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-1) var(--esp-3);
  border: 1px solid rgb(255 255 255 / 18%);
  border-radius: var(--r-redondo);
  background: transparent;
  font-size: var(--txt-nota);
  font-weight: var(--peso-rotulo);
  color: rgb(255 255 255 / 78%);
  cursor: pointer;
  transition:
    background-color var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva),
    color var(--mov-1) var(--mov-curva),
    scale var(--mov-2) var(--mov-curva);
}

.cp-nao-lida:hover {
  border-color: rgb(255 255 255 / 34%);
  background: rgb(255 255 255 / 10%);
  color: rgb(255 255 255);
}

.cp-nao-lida:active {
  scale: 0.97;
}

.cp-nao-lida:focus-visible {
  outline: 2px solid rgb(255 255 255 / 70%);
  outline-offset: 2px;
}

.cp-nao-lida__ico {
  width: var(--ico-1);
  height: var(--ico-1);
}

/* ── Abas ─────────────────────────────────────────────────────────────── */
.cp-abas {
  flex-shrink: 0;
  padding: var(--esp-2);
  border-bottom: 1px solid rgb(var(--slate-3));
  /* A superfície do painel. Era `--solid-1` / `--solid-2`: no escuro, uma
     faixa de outro preto no meio do navy. */
  background: rgb(var(--surface-2));
}

.cp-abas__caixa {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--esp-1);
}

.cp-aba {
  flex: 1;
  justify-content: center;
  gap: var(--esp-1);
  min-width: 0;
  padding: var(--esp-2) var(--esp-1);
  font-size: var(--txt-nota);
}

/* ── Linhas de informação ─────────────────────────────────────────────── */
.cp-linha {
  display: flex;
  align-items: center;
  gap: var(--esp-3);
  padding: var(--esp-3);
  border: 1px solid transparent;
  border-radius: var(--r-4);
  background: rgb(var(--slate-2));
  transition:
    background-color var(--mov-1) var(--mov-curva),
    border-color var(--mov-1) var(--mov-curva),
    translate var(--mov-2) var(--mov-curva-pulo);
}

.cp-linha:hover {
  border-color: var(--cp-borda, rgb(var(--slate-5)));
  background: var(--cp-fundo-tenue, rgb(var(--slate-3)));
  translate: 2px 0;
}

/* O ladrilho leva o tom do ASSUNTO: telefone (azul), e-mail (teal), empresa
   (íris), lugar (âmbar). */
.cp-linha__ico {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: var(--r-3);
  background: var(--cp-fundo, rgb(var(--slate-3)));
  color: var(--cp-tom, rgb(var(--slate-11)));
}

.cp-linha__ico > i {
  display: block;
  width: var(--ico-2);
  height: var(--ico-2);
  line-height: 1;
}

.cp-linha__valor {
  display: block;
  margin: 0;
  font-size: var(--txt-dado);
  color: rgb(var(--slate-12));
  overflow-wrap: anywhere;
  transition: color var(--mov-1) var(--mov-curva);
}

a.cp-linha__valor:hover {
  color: var(--cp-tom, rgb(var(--blue-11)));
}

.cp-ficha {
  text-decoration: none;
}

.cp-ficha__seta {
  flex-shrink: 0;
  width: var(--ico-2);
  height: var(--ico-2);
  color: var(--cp-tom);
  transition: translate var(--mov-2) var(--mov-curva-pulo);
}

.cp-ficha:hover .cp-ficha__seta {
  translate: 2px 0;
}

.cp-linha--azul {
  --cp-tom: rgb(var(--blue-11));
  --cp-fundo: rgb(var(--blue-3));
  --cp-fundo-tenue: rgb(var(--blue-2));
  --cp-borda: rgb(var(--blue-6));
}

.cp-linha--teal {
  --cp-tom: rgb(var(--teal-11));
  --cp-fundo: rgb(var(--teal-3));
  --cp-fundo-tenue: rgb(var(--teal-2));
  --cp-borda: rgb(var(--teal-6));
}

.cp-linha--iris {
  --cp-tom: rgb(var(--iris-11));
  --cp-fundo: rgb(var(--iris-3));
  --cp-fundo-tenue: rgb(var(--iris-2));
  --cp-borda: rgb(var(--iris-6));
}

.cp-linha--ambar {
  --cp-tom: rgb(var(--amber-11));
  --cp-fundo: rgb(var(--amber-3));
  --cp-fundo-tenue: rgb(var(--amber-2));
  --cp-borda: rgb(var(--amber-6));
}

/* ── Rótulos e seções ─────────────────────────────────────────────────────
   O rótulo é a peça do produto (`.advup-rotulo`); aqui só o respiro. */
.cp-rot {
  margin: 0 0 var(--esp-1);
}

.cp-secao {
  margin: 0 0 var(--esp-2);
  padding: 0 var(--esp-1);
}

.cp-filete {
  height: 1px;
  margin-inline: var(--esp-1);
  background: rgb(var(--slate-4));
}

/* ── Etiquetas ────────────────────────────────────────────────────────── */
.cp-etiquetas {
  display: flex;
  flex-wrap: wrap;
  gap: var(--esp-1);
  padding: 0 var(--esp-1);
}

.cp-etiqueta {
  gap: var(--esp-1);
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
  /* Etiqueta sempre em MAIÚSCULAS (Victor, 16/09/2026). */
  text-transform: uppercase;
  letter-spacing: var(--rastro-rotulo);
}

.cp-etiqueta__ponto {
  width: 8px;
  height: 8px;
  flex-shrink: 0;
  border-radius: var(--r-redondo);
}

/* ── Avatar grande ────────────────────────────────────────────────────── */
.cp-luz__fechar {
  position: absolute;
  top: var(--esp-4);
  right: var(--esp-4);
  width: 40px;
  height: 40px;
}

@media (prefers-reduced-motion: reduce) {
  .cp-avatar,
  .cp-id__txt {
    animation: none;
  }

  .cp-avatar__rosto,
  .cp-editar {
    transition: none;
  }

  .cp-hero__acao,
  .cp-nao-lida,
  .cp-linha,
  .cp-linha:hover,
  .cp-linha__valor,
  .cp-nome,
  .cp-editar {
    transition: none;
    translate: none;
  }
}

/* Chrome/Safari ignoram autocomplete="off" em campos que parecem "nome" e
   forçam texto preto + fundo claro no autofill, ilegível sobre o header navy
   escuro. Trava a cor/fundo do autofill pra bater com o resto do input. */
.cp-nome-editor:-webkit-autofill,
.cp-nome-editor:-webkit-autofill:hover,
.cp-nome-editor:-webkit-autofill:focus {
  -webkit-text-fill-color: white;
  /* O autofill do navegador pinta o próprio fundo por cima do campo; uma
     sombra interna do tamanho do campo cobre esse fundo com o véu branco do
     resto do input. Era uma transição de 9999s para "adiar" a pintura. */
  box-shadow: 0 0 0 100px rgb(255 255 255 / 10%) inset;
}
</style>
